import {
  ASSESSMENTS,
  CoverageStageStatus,
  FieldMapValue,
  getCoverageHasCallIn,
  getCoveragePath,
  getCoverageStatus,
  InsuranceCoverage,
  NextAction,
  PatientCoverageId,
  PopulatedAssessment,
  User,
  USERS,
  USERS_ADMIN,
  WithId,
} from '@hb/shared'
import { ValidationErrors } from 'final-form'
import { collection, doc, DocumentReference } from 'firebase/firestore'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { db } from '../../../backend/db/db'
import { useSubmitCoverage } from './hooks'
import { CoverageStageProps } from './types'
import { getAssessmentCoverageDataPath } from './utils'

export interface CoverageViewContextValue {
  coverageId: PatientCoverageId
  coverage: InsuranceCoverage | undefined
  isMedicaid: boolean
  optional?: boolean
  propPath: string
  // patientId: string
  nextAction: NextAction | null | undefined
  patientRef: DocumentReference<User>
  assessmentId: string | null
  assessment: PopulatedAssessment | null
  // adminPatientRef: DocumentReference<AdminUserData>
  isNew?: boolean
  handleSubmit: (data: FieldMapValue) => Promise<ValidationErrors>
  baseStoragePath: string
  adminView?: boolean
  hasCallIn: boolean
  complete: boolean
  coverageStatus: CoverageStageStatus
}

export const CoverageViewContext = createContext<CoverageViewContextValue>({
  // assessmentId: '',
  coverageId: 'primary',
  baseStoragePath: '',
  // patientId: '',
  nextAction: undefined,
  assessment: null,
  patientRef: doc(db, USERS, '__ERROR__') as DocumentReference<User>,
  // adminPatientRef: doc(db, USERS_ADMIN, '__ERROR__') as DocumentReference<AdminUserData>,
  coverage: undefined,
  handleSubmit: async () => ({}),
  isMedicaid: false,
  assessmentId: '',
  propPath: '',
  hasCallIn: false,
  complete: false,
  coverageStatus: {
    incomplete: [],
    required: [],
  },
})

export const CoverageViewProvider = ({
  children,
  coverage,
  patientRef,
  id,
  nextAction,
  assessment,
  adminView,
  assessmentId,
  pregnancyCoverageId,
  request,
}: PropsWithChildren<CoverageStageProps>) => {
  const coveragePath = useMemo(() => getCoveragePath(id), [id])
  const coverageDataPath = useMemo(
    () => (assessment ? getAssessmentCoverageDataPath(pregnancyCoverageId) : null),
    [assessment, pregnancyCoverageId],
  )
  const baseStoragePath = useMemo(() => {
    const coverageStoragePath = coveragePath.replace(/\./g, '/')
    return assessmentId && coverageDataPath
      ? `${ASSESSMENTS}/${assessmentId}/corrections/${coverageStoragePath}`
      : `${patientRef.path}/${coverageStoragePath}`
  }, [patientRef, coveragePath, assessmentId, coverageDataPath])
  const propPath = useMemo(() => getCoveragePath(id), [id])

  const adminPatientRef = useMemo(
    () => doc(collection(db, USERS_ADMIN), patientRef.id),
    [patientRef],
  )

  const hasCallIn = useMemo(
    () => getCoverageHasCallIn(coverage ? { ...coverage, id } : null, assessment, request),
    [coverage, request, id, assessment],
  )

  const requiresCallIn = useMemo(() => {
    if (adminView) return hasCallIn
    return hasCallIn && !assessment?.submittedOn
  }, [hasCallIn, assessment, adminView])

  const status = useMemo(
    () => getCoverageStatus(coverage, requiresCallIn, request),
    [coverage, requiresCallIn, request],
  )
  // const { isMedicaid } = coverage ?? {}

  const complete = useMemo(() => !status?.incomplete?.length, [status])

  // const handleSubmit = useCallback(
  //   async (data: FieldMapValue) => {
  //     if (!patientRef) {
  //       showMessage({
  //         text: 'An error occurred',
  //         subText:
  //           'Please send us a message on the contact page and we will get back to you as soon as possible',
  //         type: 'error',
  //       })
  //       return { [FORM_ERROR]: 'An error occurred' }
  //     }
  //     if (assessmentId && !assessment) {
  //       showMessage({
  //         text: 'Assessment ID provided, but assessment not found',
  //         subText: 'Get in touch with Elan',
  //         type: 'error',
  //       })
  //       return {
  //         [FORM_ERROR]: 'Assessment ID provided, but assessment not found - get in touch with Elan',
  //       }
  //     }
  //     try {
  //       const withMetadata = addMetadata(data, appName, !coverage?.createdOn)
  //       if (request && !coverage?.fromRequest) withMetadata.fromRequest = request
  //       if (request?.withCallInForm) {
  //         withMetadata.callInRequests = {
  //           [Date.now()]: addMetadata(
  //             {
  //               on: Date.now(),
  //               by: 'admin',
  //               notes: `From coverage request | ${request.message}`,
  //             },
  //             appName,
  //             true,
  //           ),
  //           ...withMetadata.callInRequests,
  //         }
  //       }
  //       if (assessmentId && coverageDataPath) {
  //         const submitted = {}
  //         nestedSet(submitted, coverageDataPath, withMetadata)
  //         await updateAssessmentAnswers({
  //           id: assessmentId,
  //           data: submitted,
  //           appName,
  //         })
  //       } else {
  //         await updateDoc(patientRef, propPath, mergeData(coverage ?? {}, withMetadata))
  //       }
  //       showMessage({
  //         type: 'success',
  //         text: `${capitalizeFirstLetter(getCoverageLabel(id, assessment))} coverage updated!`,
  //       })
  //     } catch (err: any) {
  //       showMessage({
  //         text: 'An error occurred',
  //         subText:
  //           err.message ||
  //           'Please send us a message on the contact page and we will get back to you as soon as possible',
  //         type: 'error',
  //       })
  //       return {
  //         [FORM_ERROR]:
  //           err.message ||
  //           'An error occurred. Please send us a message on the contact page and we will get back to you as soon as possible',
  //       }
  //     }
  //     return undefined
  //   },
  //   [
  //     patientRef,
  //     showMessage,
  //     propPath,
  //     coverage,
  //     appName,
  //     request,
  //     id,
  //     assessmentId,
  //     assessment,
  //     coverageDataPath,
  //   ],
  // )

  const handleSubmit = useSubmitCoverage(
    patientRef,
    id,
    coverage,
    assessmentId,
    assessment,
    pregnancyCoverageId,
    request,
  )

  // const isMedicaid = useMemo(() => {
  //   // if is primary coverage, check if the assessment is medicaid
  //   const fromCoverage = coverage?.isMedicaid
  //   if (fromCoverage !== null && fromCoverage !== undefined) return fromCoverage
  //   if (!assessment) return false
  //   if (assessment.primaryCoverageId === id)
  //     return !!assessment.data?.insuranceInfo?.primaryIsMedicaid
  //   if (assessment.secondaryCoverageId === id)
  //     return !!assessment.data?.insuranceInfo?.secondaryIsMedicaid
  //   return false
  // }, [assessment, id, coverage])

  const contextData = useMemo<CoverageViewContextValue>(
    () => ({
      coverage: coverage as WithId<InsuranceCoverage> | undefined,
      coverageId: id,
      isMedicaid: !!coverage?.isMedicaid,
      propPath,
      assessmentId,
      assessment: assessment ?? null,
      adminPatientRef,
      patientRef,
      nextAction,
      baseStoragePath,
      adminView,
      handleSubmit,
      hasCallIn,
      complete,
      coverageStatus: status,
    }),
    [
      coverage,
      propPath,
      adminView,
      handleSubmit,
      baseStoragePath,
      assessment,
      id,
      patientRef,
      adminPatientRef,
      assessmentId,
      nextAction,
      hasCallIn,
      complete,
      status,
    ],
  )

  return <CoverageViewContext.Provider value={contextData}>{children}</CoverageViewContext.Provider>
}

export const useCoverageView = () => useContext(CoverageViewContext)
